import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, ArrowRight, ClipboardList } from 'lucide-react';
import { Button } from '../../shared/ui/Button';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ErrorState } from '../../shared/ui/ErrorState';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { StatusBadge } from '../../shared/ui/Badge';
import { useRequests } from '../../features/requests/hooks';
import { fmt } from '../../shared/constants/pricing';
import { usePageMeta } from '../../shared/hooks/usePageMeta';

type Filter = 'All' | 'Pending' | 'Quoted' | 'Active' | 'Delivered' | 'Cancelled';

const FILTERS: Filter[] = ['All', 'Pending', 'Quoted', 'Active', 'Delivered', 'Cancelled'];

const ACTIVE_STATUSES = new Set(['Assigned', 'EnRoute', 'Loading', 'InTransit']);

function filterToStatus(f: Filter): string | undefined {
  if (f === 'All' || f === 'Active') return undefined;
  return f.toUpperCase();
}

const PAGE_SIZE = 20;
// "Active" combines 4 statuses client-side (the API only filters by one status at a time), so it
// can't use normal server pagination — instead it pulls a high ceiling in one shot. Comfortably
// covers a single customer's own in-flight jobs; revisit if that assumption stops holding.
const ACTIVE_FETCH_LIMIT = 200;

export function RequestsPage() {
  usePageMeta('My Requests');
  const [filter, setFilter] = useState<Filter>('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const isActive = filter === 'Active';
  const { data, isLoading, isError, refetch } = useRequests({
    status: filterToStatus(filter),
    page: isActive ? undefined : page,
    limit: isActive ? ACTIVE_FETCH_LIMIT : PAGE_SIZE,
  });

  function changeFilter(f: Filter) {
    setFilter(f);
    setPage(1);
  }

  const requests = (data?.data ?? []).filter((r) => {
    if (filter === 'Active' && !ACTIVE_STATUSES.has(r.status)) return false;
    return (
      !search ||
      r.equipmentKind.toLowerCase().includes(search.toLowerCase()) ||
      r.pickup.toLowerCase().includes(search.toLowerCase()) ||
      r.dropoff.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="flex flex-col">

      {/* Header */}
      <div className="pb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
          <h1 className="text-2xl font-black text-text">My Requests</h1>
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span className="text-xs text-muted">
            {search || isActive
              ? `${requests.length} result${requests.length !== 1 ? 's' : ''}`
              : `${data?.total ?? requests.length} result${(data?.total ?? requests.length) !== 1 ? 's' : ''}`}
          </span>
          <Button
            to="/app/requests/new"
            variant="ghost"
            size="sm"
            className="!text-accent normal-case !tracking-normal !border-2 !border-accent/30 rounded-xl hover:!bg-accent hover:!text-black"
          >
            <Plus size={13} />
            New Request
          </Button>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="py-3 flex flex-col sm:flex-row gap-3">
        <div className="relative sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search requests…"
            aria-label="Search requests"
            className="w-full rounded-xl bg-bg text-text placeholder-muted text-sm pl-9 pr-3 py-2 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-accent"
          />
        </div>
        <div className="flex overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 bg-bg sm:rounded-xl p-0.5 gap-0.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => changeFilter(f)}
              className={`flex-shrink-0 rounded-[10px] px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-all active:scale-95 ${
                filter === f
                  ? 'bg-accent text-black'
                  : 'text-muted hover:text-text'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col gap-2 mt-5">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-surface rounded-2xl p-4 flex flex-col gap-2">
              <div className="flex items-start justify-between gap-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3 w-24" />
              <div className="mt-2 pt-3 border-t border-line flex items-center justify-between gap-3">
                <Skeleton className="h-3 w-40" />
                <Skeleton className="h-3 w-3 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-12">
          <ErrorState message="Could not load requests." onRetry={() => refetch()} />
        </div>
      ) : requests.length === 0 ? (
        <div className="py-20">
          <EmptyState
            title="No requests found"
            description={
              filter === 'All'
                ? 'Create your first booking to get started.'
                : `No ${filter.toLowerCase()} requests.`
            }
            icon={<ClipboardList size={24} />}
            action={
              <Button
                to="/app/requests/new"
                variant="ghost"
                size="sm"
                className="!text-accent normal-case !tracking-normal !border-2 !border-accent/30 rounded-xl hover:!bg-accent hover:!text-black"
              >
                <Plus size={13} />
                New Request
              </Button>
            }
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2 mt-5">
          {requests.map((r, i) => (
            <AnimateIn key={r.id} animation="fade-up" delay={i * 60}>
            <Link
              to={`/app/requests/${r.id}`}
              className="group block bg-surface rounded-2xl p-4 hover:bg-surface-raised active:scale-[0.98] transition"
            >
              {/* Top: equipment name + status badge */}
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-black text-text leading-snug">{r.equipmentKind}</p>
                <StatusBadge status={r.status} className="flex-shrink-0 mt-0.5" />
              </div>

              {/* Sub: ID · date */}
              <p className="text-xs text-muted mt-1">
                <span className="font-mono">#{r.id.slice(-6).toUpperCase()}</span> · {r.dateLabel}
              </p>

              {/* Price — only present once admin sets a quote */}
              {typeof r.quotedTotal === 'number' && r.quotedTotal > 0 && (
                <p className="text-sm font-black text-accent mt-1.5">{fmt(r.quotedTotal)}</p>
              )}

              {/* Route */}
              <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs text-text truncate">{r.pickup}</p>
                  <p className="text-xs text-muted truncate mt-0.5 flex items-center gap-1">
                    <ArrowRight size={11} className="flex-shrink-0" />
                    {r.dropoff}
                  </p>
                </div>
                <ArrowRight size={13} className="text-muted flex-shrink-0 group-hover:text-accent group-hover:translate-x-0.5 transition" />
              </div>
            </Link>
            </AnimateIn>
          ))}
        </div>
      )}

      {!isActive && !isLoading && !isError && data && data.totalPages > 1 && (
        <div className="flex items-center justify-between gap-3 mt-5 pt-4 border-t border-line">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="!text-xs normal-case !tracking-normal"
          >
            Previous
          </Button>
          <span className="text-xs text-muted">
            Page {data.page} of {data.totalPages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
            disabled={page >= data.totalPages}
            className="!text-xs normal-case !tracking-normal"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
